import * as Sentry from "@sentry/react";
import { ErrorInfo } from 'react';
import { Scope, Breadcrumb } from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_TRACES_SAMPLE_RATE = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.5;
const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED;
const SENTRY_DEBUG = import.meta.env.VITE_SENTRY_DEBUG || false;
const SENTRY_ENABLE_LOGS = import.meta.env.VITE_SENTRY_ENABLE_LOGS || true;
const SENTRY_ENABLE_METRICS = import.meta.env.VITE_SENTRY_ENABLE_METRICS || true;
const SENTRY_SESSION_SAMPLE_RATE = import.meta.env.VITE_SENTRY_SESSION_SAMPLE_RATE || 0.1
const SENTRY_SAMPLE_RATE = import.meta.env.VITE_SENTRY_SAMPLE_RATE || 0.5

/**
 * Initializes monitoring service
 */
export const initializeSentry = (): void => {
    Sentry.init({
        dsn: SENTRY_DSN,
        debug: SENTRY_DEBUG,
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        enabled: SENTRY_ENABLED,
        enableLogs: SENTRY_ENABLE_LOGS,
        beforeSendLog: (log) => {
            // Drop debug logs in production
            if (log.level === "debug") {
                return null;
            }
            // Remove sensitive attributes
            if (log.attributes?.password) {
                delete log.attributes.password;
            }
            if (log.attributes?.userId) {
                delete log.attributes.userId;
            }
            return log;
        },
        enableMetrics: SENTRY_ENABLE_METRICS,
        tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
        ],
        // Session Replay
        // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysSessionSampleRate: SENTRY_SESSION_SAMPLE_RATE,
        // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
        replaysOnErrorSampleRate: SENTRY_SAMPLE_RATE
    });
};

/**
 * capture and log any errors caught
 * @param error error in stacktrace
 * @param errorInfo Error information from React
 */
export const captureAndLogSentryError = (error: Error, errorInfo: ErrorInfo): void => {
    if (SENTRY_ENABLED) {
        Sentry.withScope((scope: Scope) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            scope.setExtras(errorInfo);
            captureSentryException(error, scope, error.message);
        });
    }
};

/**
 * Capture exception
 * @param {Error} error Error context
 * @param scope
 * @param errorMessage
 */
export const captureSentryException = (
    error: Error,
    scope?: Scope,
    errorMessage = 'Error Caught',
): void => {
    if (SENTRY_ENABLED) {
        Sentry.captureMessage(errorMessage, scope);
        Sentry.captureException(error);
    }
};

export const captureSentryLog = (
    message: string,
    logLevel: LogLevel,
    attributes?: Record<string, unknown>
): void => {
    if (SENTRY_ENABLED) {
        switch (logLevel) {
            case "info": {
                Sentry.logger.info(message, attributes);
                break;
            }
            case "debug": {
                Sentry.logger.debug(message, attributes);
                break;
            }
            case "warn": {
                Sentry.logger.warn(message, attributes);
                break;
            }
            case "error": {
                Sentry.logger.error(message, attributes);
                break;
            }
            case "fatal": {
                Sentry.logger.fatal(message, attributes);
                break;
            }
            case "trace": {
                Sentry.logger.trace(message, attributes);
                break;
            }
            default: {
                break;
            }
        }
    }
};

export type SentryBreadcrumb = Breadcrumb;
export type SentryScope = Scope;